import {
  Pressable,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import RNModal from 'react-native-modal';
import { styles } from './style';
import { IS_WEB } from '@/utils/platform';

interface ModalProps {
  title?: string;
  isVisible: boolean;
  onClose?: () => void;
  confirmActionButton?: React.ReactNode;
  cancelActionButton?: React.ReactNode;
  children: React.ReactNode;
  onModalHide?: () => void;
  coverKeyboard?: boolean;
  animationInTiming?: number;
  animationOutTiming?: number;
  customStyle?: StyleProp<ViewStyle>;
  isContentScrollable?: boolean;
  testID?: string;
  wide?: boolean;
}

/*
 * This Modal component is based on react-native-modal
 * Provides a customizable modal with title, close button, and action buttons.
 * Limitation: Can't display multiple modals at the same time. If
 * this is needed, you will have to use a setTimeout to show the next modal after the first one is closed.
 */
export const Modal = ({
  title,
  isVisible,
  onClose,
  confirmActionButton,
  cancelActionButton,
  children,
  animationInTiming,
  animationOutTiming,
  onModalHide,
  customStyle,
  isContentScrollable,
  testID,
  wide,
}: ModalProps) => {
  return (
    <RNModal
      isVisible={isVisible}
      avoidKeyboard
      propagateSwipe
      style={[
        wide ? styles.containerWide : styles.container,
        !IS_WEB && { margin: 0 },
      ]}
      statusBarTranslucent
      animationInTiming={animationInTiming}
      animationOutTiming={animationOutTiming}
      onModalHide={onModalHide}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <SafeAreaView
        style={[styles.modal, wide && styles.modalWide, customStyle]}
        testID={testID}
      >
        {onClose ? (
          <Pressable
            onPress={onClose}
            style={styles.closeButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Icon
              name="x"
              size={24}
              style={{
                color: colors.newUI.therapist.primaryBlue80,
              }}
            />
          </Pressable>
        ) : null}
        <View
          style={[
            !IS_WEB && !wide && { maxWidth: '80%' },
            { flexShrink: 1 },
            wide && styles.contentWide,
          ]}
        >
          {!!title && <Text style={styles.title}>{title}</Text>}
          {isContentScrollable ? (
            <ScrollView style={{ padding: 2 }}>{children}</ScrollView>
          ) : (
            children
          )}
          <View
            style={
              cancelActionButton && confirmActionButton
                ? styles.actionButtons
                : styles.singleActionButton
            }
          >
            {cancelActionButton ? <View>{cancelActionButton}</View> : null}
            {confirmActionButton ? <View>{confirmActionButton}</View> : null}
          </View>
        </View>
      </SafeAreaView>
    </RNModal>
  );
};
